import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Platform, View } from 'react-native';

import { Box } from '@fhs-legacy/universal-components';
import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { DeliveryTerms } from 'components/address-modal/delivery-terms';
import { SwitchServiceModeDialog } from 'components/switch-service-mode-dialog';
import { StoreChangeDisclaimer } from 'pages/store-locator/store-change-disclaimer';
import { useOrderContext } from 'state/order';
import { ServiceMode, useServiceModeContext } from 'state/service-mode';

import { ButtonContainer, DeliveryInfoContainer, LoadingText } from './styled';

interface IDeliveryFormFooterProps {
  submitAddress: () => void;
  setCreateAddressView: (show: boolean) => void;
  showDeliveryAddresses: boolean;
  isDisabled: boolean;
  isLoading: boolean;
  /** Web-only: ref attached to the wrapper around the "Deliver Here" button so callers can move focus to it. */
  deliverHereRef?: React.RefObject<View>;
}

const DeliveryAddressFormFooter: React.FC<React.PropsWithChildren<IDeliveryFormFooterProps>> = ({
  submitAddress,
  setCreateAddressView,
  showDeliveryAddresses,
  isDisabled,
  isLoading,
  deliverHereRef,
}) => {
  const [showChangeToPickUpDialog, setShowChangeToPickUpDialog] = useState<boolean>(false);
  const { formatMessage } = useIntl();
  const deliveryCheckingMessage = formatMessage({ id: 'deliveryCheckingMessage' });
  const deliverHereLabel = formatMessage({ id: 'deliverHere' });
  const deliverHereDisabledA11yLabel = formatMessage({
    id: 'deliverHereDisabledAccessibilityLabel',
  });
  const { serviceMode } = useServiceModeContext();

  const { cartEntries } = useOrderContext();

  const switchToDeliveryAction = () => {
    submitAddress();
  };

  const switchModeAction = () => {
    const isDelivery = serviceMode === ServiceMode.DELIVERY;
    // cartEntries will always be 0 because every time we change from one service mode to another,
    // we clear the cart in 'SwitchServiceModeDialog', so showChangeToPickUpDialog will never be true
    // showChangeToPickUpDialog needs to be true to display the component that uses switchToDeliveryAction 
    if (!isDelivery && Array.isArray(cartEntries) && cartEntries.length > 0) {
      setShowChangeToPickUpDialog(true);
    } else {
      submitAddress();
    }
  };

  return (
    <>
      <DeliveryInfoContainer>
        <StoreChangeDisclaimer />
        <DeliveryTerms />
      </DeliveryInfoContainer>
      <Box>
        <View ref={deliverHereRef}>
          <ButtonContainer>
            <ActionButton
              onPress={switchModeAction}
              fullWidth
              disabled={isLoading || isDisabled}
              perceptible={isDisabled && !isLoading}
              accessibilityLabel={
                isDisabled && !isLoading ? deliverHereDisabledA11yLabel : undefined
              }
              isLoading={isLoading}
              testID="deliver-here"
              _text={{ fontSize: 'lg' }}
            >
              {deliverHereLabel}
            </ActionButton>
          </ButtonContainer>
        </View>
        {showDeliveryAddresses && (
          <ButtonContainer>
            <ActionButton
              testID="back-to-saved-addresses"
              fullWidth
              variant={ActionButtonVariants.OUTLINE}
              onPress={() => setCreateAddressView(false)}
              _text={{ fontSize: 'md' }}
            >
              {formatMessage({ id: 'backToSavedAddresses' })}
            </ActionButton>
          </ButtonContainer>
        )}
      </Box>
      <Box
        testID="delivery-checking-status"
        accessible={isLoading}
        accessibilityLabel={isLoading ? deliveryCheckingMessage : undefined}
        accessibilityRole={Platform.OS !== 'web' ? 'text' : undefined}
        accessibilityLiveRegion="polite"
        _web={
          {
            role: 'status',
            'aria-live': 'polite',
            'aria-atomic': true,
            'aria-busy': isLoading,
          } as React.ComponentProps<typeof Box>['_web']
        }
      >
        {isLoading && <LoadingText accessible={false}>{deliveryCheckingMessage}</LoadingText>}
      </Box>
      {showChangeToPickUpDialog && (
        <SwitchServiceModeDialog
          showDialog
          onConfirm={switchToDeliveryAction}
          onDismiss={() => setShowChangeToPickUpDialog(false)}
          switchingFromPickUpToDelivery={true}
        />
      )}
    </>
  );
};

export default DeliveryAddressFormFooter;
